/**
 * Dynamic ribbon management.
 *
 * Uses Office.ribbon.requestUpdate() (RibbonApi 1.1) to enable/disable
 * ribbon buttons based on the detected Excel context.
 *
 * Gracefully degrades on hosts that don't support RibbonApi 1.1:
 * buttons remain always enabled (manifest default behavior).
 */

/* global Office */

import type { ExcelContextState } from './excel-context';

/** Check whether the host supports dynamic ribbon updates. */
export function isRibbonApiSupported(): boolean {
  try {
    return Office.context.requirements.isSetSupported('RibbonApi', '1.1');
  } catch {
    return false;
  }
}

/**
 * Update ribbon button enabled state based on the current context.
 *
 * Control IDs match the manifest exactly:
 *   Always enabled: Assistant.Open, Formulas.Generate, Functions.Open, Help.Open, Settings.Open
 *   Dynamic:        Formulas.Explain, Cleaning.Open, Analysis.Open, Chart.Open, Summary.Open, Translate.Open
 *
 * No-op if RibbonApi 1.1 is not supported.
 */
export async function updateRibbonByContext(
  state: ExcelContextState,
): Promise<void> {
  if (!isRibbonApiSupported()) return;

  try {
    await Office.ribbon.requestUpdate({
      tabs: [
        {
          id: 'ExcelAIStudio.Tab',
          controls: [
            { id: 'Assistant.Open', enabled: true },
            { id: 'Formulas.Generate', enabled: true },
            { id: 'Formulas.Explain', enabled: state.isFormula },
            { id: 'Cleaning.Open', enabled: state.hasSelection },
            { id: 'Analysis.Open', enabled: state.isDataset },
            { id: 'Chart.Open', enabled: state.canChart },
            { id: 'Summary.Open', enabled: state.hasSelection },
            { id: 'Translate.Open', enabled: state.isTextual },
            { id: 'Functions.Open', enabled: true },
            { id: 'Help.Open', enabled: true },
            { id: 'Settings.Open', enabled: true },
          ],
        },
      ],
    });
  } catch (err) {
    console.warn('[ribbon] requestUpdate failed:', err);
  }
}
